export type ValidationKind = 'text' | 'email' | 'phone' | 'password' | 'number' | 'date' | 'select' | 'textarea';

export interface ValidationRules {
  required?: boolean;
  kind?: ValidationKind;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  integer?: boolean;
  pattern?: RegExp;
  patternMessage?: string;
  noFutureDate?: boolean;
  noPastDate?: boolean;
}

export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  phone: /^\d{10}$/,
  safeText: /^[A-Za-z0-9][A-Za-z0-9 .,_/#&()+-]*$/,
  code: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,64}$/
};

export function normalizeText(value: unknown, lowercase = false) {
  const normalized = String(value ?? '').trim().replace(/\s+/g, ' ');
  return lowercase ? normalized.toLowerCase() : normalized;
}

export function sanitizeText(value: unknown) {
  return normalizeText(value).replace(/[<>]/g, '');
}

export function hasValue(value: unknown) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== '';
}

export function validateValue(label: string, value: unknown, rules: ValidationRules = {}) {
  if (rules.required && !hasValue(value)) return `${label} is required`;
  if (!hasValue(value)) return '';

  const kind = rules.kind || 'text';
  const textValue = typeof value === 'string' ? normalizeText(value, kind === 'email') : String(value ?? '');

  if ((kind === 'text' || kind === 'textarea') && textValue.length === 0) return `${label} cannot contain only spaces`;
  if (rules.minLength && textValue.length < rules.minLength) return `${label} must contain at least ${rules.minLength} characters`;
  if (rules.maxLength && textValue.length > rules.maxLength) return `${label} must not exceed ${rules.maxLength} characters`;
  if (kind === 'email' && !validationPatterns.email.test(textValue)) return 'Please enter a valid email address';
  if (kind === 'phone' && !validationPatterns.phone.test(textValue)) return 'Phone number must contain only digits and be 10 digits long';
  if (kind === 'password') {
    if (textValue.length < (rules.minLength || 8)) return `Password must contain at least ${rules.minLength || 8} characters`;
    if (textValue.length > (rules.maxLength || 64)) return `Password must not exceed ${rules.maxLength || 64} characters`;
    if (!/[A-Z]/.test(textValue)) return 'Password must include an uppercase letter';
    if (!/[a-z]/.test(textValue)) return 'Password must include a lowercase letter';
    if (!/\d/.test(textValue)) return 'Password must include a number';
    if (!/[^A-Za-z0-9]/.test(textValue)) return 'Password must include a special character';
  }
  if (kind === 'number') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${label} must be a valid number`;
    if (rules.integer && !Number.isInteger(numeric)) return `${label} must be a whole number`;
    if (rules.min !== undefined && numeric < rules.min) return `${label} must be at least ${rules.min}`;
    if (rules.max !== undefined && numeric > rules.max) return `${label} must not exceed ${rules.max}`;
  }
  if (kind === 'date') {
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) return `${label} must be a valid date`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const candidate = new Date(date);
    candidate.setHours(0, 0, 0, 0);
    if (rules.noFutureDate && candidate > today) return `${label} cannot be a future date`;
    if (rules.noPastDate && candidate < today) return `${label} cannot be a past date`;
  }
  if (rules.pattern && !rules.pattern.test(textValue)) return rules.patternMessage || `${label} contains invalid characters`;
  return '';
}
